import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { PageHeader } from '../components/PageHeader';
import { OrganogramSection } from '../components/OrganogramSection';
import { SEO } from '../components/SEO';
import { Users, Target, Shield, Award } from 'lucide-react';
export function StructurePage() {
  return <div className="font-sans bg-warm-ivory min-h-screen w-full" data-id="element-854">
      <SEO title="Organizational Structure - SFRR Foundation" description="Learn about SFRR Foundation's leadership team, governance structure, and organizational framework. Transparency and accountability in action." keywords="SFRR leadership, foundation structure, board of trustees, organizational governance, nonprofit management Nigeria" data-id="element-855" />

      <Header data-id="element-856" />
      <main className="w-full font-sans" data-id="element-857">
        <PageHeader title="Our Organizational Structure" subtitle="Meet the dedicated team leading our mission across Nigeria" badgeText="STRUCTURE" backgroundImage="https://cdn.magicpatterns.com/uploads/2TJ6UkgbUQGJTAPF9Zz8N9/SFRR_Foundation_(4).jpg" data-id="element-858" />

        {/* Introduction */}
        <section className="py-16 bg-white" data-id="element-859">
          <div className="container mx-auto px-4" data-id="element-860">
            <div className="max-w-3xl mx-auto text-center" data-id="element-861">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-charcoal mb-6" data-id="element-862">
                Built on Strong Foundations
              </h2>
              <p className="text-lg text-neutral-600 leading-relaxed" data-id="element-863">
                Our organizational structure ensures transparency,
                accountability, and effective delivery of programs across all
                six focus areas. Every team member is committed to our mission
                of transforming lives and building futures.
              </p>
            </div>
          </div>
        </section>

        {/* Core Principles */}
        <section className="py-16 bg-gradient-to-b from-warm-ivory to-white" data-id="element-864">
          <div className="container mx-auto px-4" data-id="element-865">
            <div className="max-w-6xl mx-auto" data-id="element-866">
              <div className="text-center mb-12" data-id="element-867">
                <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4" data-id="element-868">
                  Our Principles
                </span>
                <h2 className="text-2xl md:text-3xl font-serif font-bold text-charcoal mb-4" data-id="element-869">
                  How We Operate
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-870">
                {[{
                icon: <Shield className="w-10 h-10" data-id="element-871" />,
                title: 'Transparency',
                description: 'Open reporting on all programs, finances, and impact metrics',
                color: 'bg-primary'
              }, {
                icon: <Users className="w-10 h-10" data-id="element-872" />,
                title: 'Collaboration',
                description: 'Working with communities, government, and partners',
                color: 'bg-accent'
              }, {
                icon: <Target className="w-10 h-10" data-id="element-873" />,
                title: 'Accountability',
                description: 'Clear roles, responsibilities, and measurable outcomes',
                color: 'bg-secondary'
              }, {
                icon: <Award className="w-10 h-10" data-id="element-874" />,
                title: 'Excellence',
                description: 'Highest standards in program delivery and governance',
                color: 'bg-primary'
              }].map((principle, index) => <div key={index} className="bg-white rounded-2xl p-8 shadow-soft hover:shadow-hover transition-all duration-300 hover:-translate-y-1 text-center animate-fadeInUp" style={{
                animationDelay: `${index * 100}ms`
              }} data-id="element-875">
                    <div className={`w-16 h-16 rounded-xl ${principle.color} bg-opacity-10 flex items-center justify-center mx-auto mb-6`} data-id="element-876">
                      <div className={`${principle.color.replace('bg-', 'text-')}`} data-id="element-877">
                        {principle.icon}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-charcoal mb-3" data-id="element-878">
                      {principle.title}
                    </h3>
                    <p className="text-neutral-600 text-sm leading-relaxed" data-id="element-879">
                      {principle.description}
                    </p>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        <OrganogramSection data-id="element-880" />

        {/* Governance */}
        <section className="py-16 bg-white" data-id="element-881">
          <div className="container mx-auto px-4" data-id="element-882">
            <div className="max-w-4xl mx-auto" data-id="element-883">
              <div className="text-center mb-12" data-id="element-884">
                <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4" data-id="element-885">
                  Governance
                </span>
                <h2 className="text-2xl md:text-3xl font-serif font-bold text-charcoal mb-4" data-id="element-886">
                  Commitment to Excellence
                </h2>
                <p className="text-neutral-600" data-id="element-887">
                  Our governance framework ensures ethical operations and
                  maximum impact
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-888">
                <div className="bg-warm-ivory rounded-2xl p-8 border border-neutral-100" data-id="element-889">
                  <h3 className="text-xl font-bold text-charcoal mb-4" data-id="element-890">
                    Board of Trustees
                  </h3>
                  <p className="text-neutral-600 mb-4" data-id="element-891">
                    Our board provides strategic oversight, ensures compliance
                    with regulations, and upholds our commitment to transparency
                    and accountability.
                  </p>
                  <ul className="space-y-2 text-sm text-neutral-600" data-id="element-892">
                    <li data-id="element-893">• Quarterly board meetings</li>
                    <li data-id="element-894">• Annual financial audits</li>
                    <li data-id="element-895">• Strategic planning oversight</li>
                    <li data-id="element-896">• Risk management</li>
                  </ul>
                </div>

                <div className="bg-warm-ivory rounded-2xl p-8 border border-neutral-100" data-id="element-897">
                  <h3 className="text-xl font-bold text-charcoal mb-4" data-id="element-898">
                    Advisory Council
                  </h3>
                  <p className="text-neutral-600 mb-4" data-id="element-899">
                    Expert advisors from various sectors provide guidance on
                    program development, partnerships, and community engagement
                    strategies.
                  </p>
                  <ul className="space-y-2 text-sm text-neutral-600" data-id="element-900">
                    <li data-id="element-901">• Subject matter experts</li>
                    <li data-id="element-902">• Community representatives</li>
                    <li data-id="element-903">• Technical advisors</li>
                    <li data-id="element-904">• Partnership facilitators</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Registration & Compliance */}
        <section className="py-16 bg-gradient-to-br from-primary/5 to-secondary/5" data-id="element-905">
          <div className="container mx-auto px-4" data-id="element-906">
            <div className="max-w-3xl mx-auto text-center" data-id="element-907">
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-charcoal mb-6" data-id="element-908">
                Registered & Compliant
              </h2>
              <p className="text-lg text-neutral-600 mb-8" data-id="element-909">
                The Senator Fatimat Raji Rasaki Foundation is a registered
                non-profit organization operating in full compliance with
                Nigerian regulations.
              </p>
              <div className="bg-white rounded-2xl p-8 shadow-soft inline-block" data-id="element-910">
                <p className="text-sm text-neutral-600 mb-2" data-id="element-911">
                  Registration Number
                </p>
                <p className="text-3xl font-bold text-primary" data-id="element-912">RC 157857</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-913" />
    </div>;
}